'use client';
import React, { useEffect, useState } from 'react';
import { MapPin, Users, CheckCircle, Info } from 'lucide-react';
import type { OrderState } from './OrderWizardContent';
import { createClient } from '@/lib/supabase/client';
import { FLORIDA_REGIONS, PUERTO_RICO_REGIONS } from '@/lib/catalogData';

interface RegionOption {
  id: string;
  name: string;
  cities: string[];
  households: number;
}

interface Props {
  order: OrderState;
  updateOrder: (updates: Partial<OrderState>) => void;
  showErrors?: boolean;
}

export default function StepB({ order, updateOrder, showErrors }: Props) {
  const [flRegions, setFlRegions] = useState<RegionOption[]>(FLORIDA_REGIONS);
  const [prRegions, setPrRegions] = useState<RegionOption[]>(PUERTO_RICO_REGIONS);
  const [regionsLoading, setRegionsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const loadRegions = async () => {
      setRegionsLoading(true);
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('regions')
          .select('id, name, market, cities, households')
          .eq('is_active', true)
          .order('sort_order', { ascending: true });

        if (cancelled) return;

        if (error || !data?.length) {
          // Fall back to static data
          setRegionsLoading(false);
          return;
        }

        const fl: RegionOption[] = data
          .filter((r: any) => r.market === 'FL')
          .map((r: any) => ({
            id: r.id,
            name: r.name,
            cities: Array.isArray(r.cities) ? r.cities : [],
            households: r.households ?? 1000,
          }));

        const pr: RegionOption[] = data
          .filter((r: any) => r.market === 'PR')
          .map((r: any) => ({
            id: r.id,
            name: r.name,
            cities: Array.isArray(r.cities) ? r.cities : [],
            households: r.households ?? 1000,
          }));

        if (fl.length) setFlRegions(fl);
        if (pr.length) setPrRegions(pr);
      } catch {
        // Non-fatal: static fallback remains
      } finally {
        if (!cancelled) setRegionsLoading(false);
      }
    };

    loadRegions();
    return () => { cancelled = true; };
  }, []);

  const regions = order.market === 'florida' ? flRegions : prRegions;

  const toggleRegion = (id: string) => {
    const next = order.regions.includes(id)
      ? order.regions.filter(r => r !== id)
      : [...order.regions, id];
    updateOrder({ regions: next });
  };

  const selectAll = () => updateOrder({ regions: regions.map(r => r.id) });
  const clearAll = () => updateOrder({ regions: [] });

  const getDiscountLabel = (count: number) => {
    if (count >= 8) return '15% off standard pages';
    if (count >= 5) return '10% off standard pages';
    if (count >= 3) return '5% off standard pages';
    return null;
  };

  const discountLabel = getDiscountLabel(order.regions.length);

  return (
    <div className="animate-slide-up">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-700 text-white mb-1">Select Regions</h2>
        <p className="text-sm text-slate-400">
          Choose one or more regions. Each region receives 1,000 copies. Multi-region discounts apply to standard inside pages.
        </p>
      </div>

      {/* Action row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {order.regions.length > 0 && (
            <span className="chip chip-selected">{order.regions.length} region{order.regions.length > 1 ? 's' : ''} selected</span>
          )}
          {discountLabel && (
            <span className="chip bg-green-500/15 border-green-500/25 text-green-400 text-xs">
              {discountLabel}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={selectAll} className="text-xs text-brand-400 hover:text-brand-300 transition-colors font-medium">Select all</button>
          <span className="text-slate-600">·</span>
          <button onClick={clearAll} className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Clear</button>
        </div>
      </div>

      {/* Loading state */}
      {regionsLoading && (
        <div className="flex items-center justify-center py-8">
          <div className="w-5 h-5 border-2 border-brand-500/30 border-t-brand-400 rounded-full animate-spin" />
        </div>
      )}

      {/* Region grid */}
      {!regionsLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          {regions.map((region) => {
            const selected = order.regions.includes(region.id);
            return (
              <button
                key={`wizard-region-${region.id}`}
                onClick={() => toggleRegion(region.id)}
                className={`text-left p-4 rounded-xl border transition-all duration-200 ${
                  selected
                    ? 'border-brand-500/60 bg-brand-600/12 shadow-glow-sm'
                    : 'border-white/10 bg-navy-700/40 hover:border-brand-500/30 hover:bg-white/3'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MapPin size={13} className={selected ? 'text-brand-400' : 'text-slate-600'} />
                    <span className="text-sm font-semibold text-white leading-tight">{region.name}</span>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all ${
                    selected ? 'bg-brand-500 border-brand-500' : 'border-white/20'
                  }`}>
                    {selected && <CheckCircle size={11} className="text-white" />}
                  </div>
                </div>
                <div className="flex flex-wrap gap-1">
                  {region.cities.slice(0, 3).map((city) => (
                    <span key={`wizard-city-${region.id}-${city}`} className="chip" style={{ fontSize: '10px' }}>{city}</span>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Users size={10} className="text-slate-600" />
                  <span className="text-xs text-slate-600">{region.households.toLocaleString()} copies / issue</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {!regionsLoading && order.regions.length === 0 && (
        <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${showErrors ? 'bg-red-500/8 border-red-500/25' : 'bg-amber-500/8 border-amber-500/20'}`}>
          <Info size={14} className={`shrink-0 mt-0.5 ${showErrors ? 'text-red-400' : 'text-amber-400'}`} />
          <p className="text-xs text-slate-400">
            {showErrors ? 'At least one region is required to continue.' : 'Select at least one region to continue.'}
          </p>
        </div>
      )}
    </div>
  );
}